import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiUser,
    FiPlus, FiZoomIn, FiZoomOut, FiBan, FiList, FiGrid,
    FiRefreshCw, FiAlertTriangle, FiCheck, FiSearch, FiFilter,
    FiSun, FiMoon, FiUsers
} from "react-icons/fi";

import { appointmentsAPI, usersAPI, servicesAPI, workScheduleAPI, authAPI } from "services/api";
import AppointmentDetailModal from "./components/AppointmentDetailModal";
import QuickCreateModal from "./components/QuickCreateModal";
import BreakModal from "./components/BreakModal";
import {BsBan} from "react-icons/bs";

// ============================================================
// YARDIMCI FONKSİYONLAR
// ============================================================

const formatISODate = (date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
};

const timeToFloat = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours + minutes / 60;
};

const addMinutesToTime = (timeStr, minutesToAdd) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    date.setMinutes(date.getMinutes() + minutesToAdd);
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const getInitials = (name = "", surname = "") =>
    ((name[0] || "") + (surname[0] || "")).toUpperCase() || "?";

const BUSINESS_START = 9;
const BUSINESS_END = 23;

const STATUS_CONFIG = {
    pending:   { label: "Beklemede", color: "#f59f00", bg: "rgba(245,159,0,0.14)",   border: "#f59f00" },
    approved:  { label: "Onaylandı", color: "#10b981", bg: "rgba(16,185,129,0.14)",  border: "#10b981" },
    completed: { label: "Tamamlandı",color: "#0ea5e9", bg: "rgba(14,165,233,0.14)",  border: "#0ea5e9" },
    cancelled: { label: "İptal",     color: "#ef4444", bg: "rgba(239,68,68,0.14)",   border: "#ef4444" },
    no_show:   { label: "Gelmedi",   color: "#f97316", bg: "rgba(249,115,22,0.14)",  border: "#f97316" },
    mola:      { label: "Mola",      color: "#94a3b8", bg: "rgba(148,163,184,0.14)", border: "#94a3b8" },
};

const getStatusCfg = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.pending;

// ============================================================
// SKELETON LOADER
// ============================================================
const CalendarSkeleton = () => (
    <div className="animate-pulse space-y-3 p-4">
        {[...Array(8)].map((_, i) => (
            <div key={i} className="flex gap-3">
                <div className="w-14 h-16 bg-gray-200 dark:bg-navy-700 rounded-xl shrink-0"></div>
                <div className="flex-1 flex gap-2">
                    {[...Array(4)].map((_, j) => (
                        <div key={j} className="flex-1 h-16 bg-gray-200 dark:bg-navy-700 rounded-xl"></div>
                    ))}
                </div>
            </div>
        ))}
    </div>
);

// ============================================================
// APPOINTMENT CARD (TAKVİM İÇİ KART)
// ============================================================
const AppointmentCard = ({ appt, zoomLevel, count, index, onClick, isStaffColumn }) => {
    const statusCfg = getStatusCfg(appt.status);

    let totalDuration = 0;
    if (appt.appointment_duration) {
        totalDuration = parseInt(appt.appointment_duration);
    } else {
        const sDur = parseInt(appt.service?.duration || 30);
        const o1Dur = appt.optionalService1 ? parseInt(appt.optionalService1.duration || 0) : 0;
        const o2Dur = appt.optionalService2 ? parseInt(appt.optionalService2.duration || 0) : 0;
        totalDuration = sDur + o1Dur + o2Dur;
    }

    const startMin = parseInt((appt.appointment_time || "00:00").split(":")[1] || 0);
    const topPos = (startMin / 60) * zoomLevel;
    const heightPx = Math.max((totalDuration / 60) * zoomLevel, 28);
    const widthPercent = 100 / count;
    const leftPercent = widthPercent * index;
    const smartZIndex = 50 - Math.min(Math.floor(totalDuration / 15), 40);

    const endTimeStr = addMinutesToTime(appt.appointment_time?.substring(0, 5), totalDuration);
    const mainServiceName = appt.service_name || appt.service?.name;

    return (
        <div
            style={{
                position: "absolute",
                top: `calc(${topPos}px - 1px)`,
                height: `${heightPx + 1}px`,
                left: `calc(${leftPercent}% + 2px)`,
                width: `calc(${widthPercent}% - 4px)`,
                zIndex: smartZIndex,
                backgroundColor: statusCfg.bg,
                borderLeft: `3px solid ${statusCfg.border}`,
                borderRadius: "10px",
                overflow: "hidden",
                cursor: "pointer",
                backdropFilter: "blur(4px)",
                transition: "all 0.18s ease",
                boxShadow: `0 2px 8px ${statusCfg.color}20`,
                padding: "5px 8px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
            }}
            onClick={(e) => { e.stopPropagation(); onClick(appt); }}
            onMouseEnter={e => {
                e.currentTarget.style.zIndex = 100;
                e.currentTarget.style.width = "calc(100% - 4px)";
                e.currentTarget.style.left = "2px";
                e.currentTarget.style.boxShadow = `0 8px 24px ${statusCfg.color}35`;
                e.currentTarget.style.transform = "scaleY(1.01)";
            }}
            onMouseLeave={e => {
                e.currentTarget.style.zIndex = smartZIndex;
                e.currentTarget.style.width = `calc(${widthPercent}% - 4px)`;
                e.currentTarget.style.left = `calc(${leftPercent}% + 2px)`;
                e.currentTarget.style.boxShadow = `0 2px 8px ${statusCfg.color}20`;
                e.currentTarget.style.transform = "scaleY(1)";
            }}
        >
            {/* No show badge */}
            {appt.status === "no_show" && (
                <span
                    className="absolute top-1 right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-md text-white"
                    style={{ backgroundColor: STATUS_CONFIG.no_show.color }}
                >GELMEDİ</span>
            )}

            {/* Mola badge */}
            {appt.is_mola && (
                <span className="absolute top-1 right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-md text-white bg-gray-400">MOLA</span>
            )}

            <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] font-bold truncate" style={{ color: statusCfg.color }}>
          {appt.appointment_time?.substring(0, 5)} - {endTimeStr}
        </span>
                {!isStaffColumn && appt.staff && (
                    <span className="text-[9px] font-bold bg-black/10 dark:bg-white/10 rounded px-1 text-gray-700 dark:text-gray-200 shrink-0">
            {(appt.staff.name?.substring(0, 2) || "").toUpperCase()}.
          </span>
                )}
            </div>

            {!appt.is_mola && (
                <div className="text-[11px] font-bold truncate text-navy-700 dark:text-white mt-0.5">
                    {appt.customer?.name} {appt.customer?.surname}
                </div>
            )}

            {heightPx > 45 && !appt.is_mola && mainServiceName && (
                <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {mainServiceName}
                </div>
            )}
        </div>
    );
};

// ============================================================
// MOBİL KART (Listeli görünüm)
// ============================================================
const MobileAppointmentCard = ({ appt, onPress }) => {
    const statusCfg = getStatusCfg(appt.status);

    let totalDuration = 0;
    if (appt.appointment_duration) {
        totalDuration = parseInt(appt.appointment_duration);
    } else {
        const sDur = parseInt(appt.service?.duration || 30);
        const o1Dur = appt.optionalService1 ? parseInt(appt.optionalService1.duration || 0) : 0;
        const o2Dur = appt.optionalService2 ? parseInt(appt.optionalService2.duration || 0) : 0;
        totalDuration = sDur + o1Dur + o2Dur;
    }

    const opt1 = appt.optional_service_1 || appt.optionalService1;
    const opt2 = appt.optional_service_2 || appt.optionalService2;
    const endTimeStr = addMinutesToTime(appt.appointment_time?.substring(0, 5), totalDuration);

    if (appt.is_mola) {
        return (
            <div
                className="p-4 rounded-2xl border border-gray-200 dark:border-navy-700 bg-gray-50 dark:bg-navy-900 cursor-pointer hover:shadow-md transition-all"
                onClick={() => onPress(appt)}
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-300 dark:bg-navy-700 flex items-center justify-center">
                        <BsBan className="text-gray-500 dark:text-gray-400" />
                    </div>
                    <div>
                        <div className="font-bold text-gray-600 dark:text-gray-300 text-sm">MOLA (KAPALI)</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-0.5">
                            <FiClock size={11} />
                            {appt.appointment_time?.substring(0, 5)} - {endTimeStr}
                            {appt.staff && <span className="ml-1">• {appt.staff.name} {appt.staff.surname}</span>}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="rounded-2xl border cursor-pointer hover:shadow-lg transition-all overflow-hidden"
            style={{ borderColor: statusCfg.border + "60", backgroundColor: statusCfg.bg }}
            onClick={() => onPress(appt)}
        >
            <div className="flex items-stretch">
                {/* Sol renkli şerit */}
                <div className="w-1.5 shrink-0 rounded-l-2xl" style={{ backgroundColor: statusCfg.border }}></div>

                <div className="flex-1 p-4">
                    {/* Üst satır: saat + status */}
                    <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold flex items-center gap-1" style={{ color: statusCfg.color }}>
              <FiClock size={11} />
                {appt.appointment_time?.substring(0, 5)} - {endTimeStr}
                <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">({totalDuration} dk)</span>
            </span>
                        <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ color: statusCfg.color, backgroundColor: statusCfg.bg }}
                        >
              {statusCfg.label}
            </span>
                    </div>

                    {/* Müşteri */}
                    <div className="flex items-center gap-2 mb-2">
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                            style={{ backgroundColor: statusCfg.border }}
                        >
                            {getInitials(appt.customer?.name, appt.customer?.surname)}
                        </div>
                        <div>
                            <div className="font-bold text-navy-700 dark:text-white text-sm">
                                {appt.customer?.name} {appt.customer?.surname}
                            </div>
                            {appt.customer?.phone && (
                                <div className="text-[11px] text-gray-400 dark:text-gray-500">{appt.customer.phone}</div>
                            )}
                        </div>
                    </div>

                    {/* Hizmet */}
                    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                        <span className="font-semibold">{appt.service?.name || appt.service_name}</span>
                        {opt1 && <span className="text-brand-500 font-semibold">+ {opt1.name}</span>}
                        {opt2 && <span className="text-brand-500 font-semibold">+ {opt2.name}</span>}
                    </div>

                    {/* Personel */}
                    {appt.staff && (
                        <div className="mt-2 flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                            <FiUser size={11} />
                            {appt.staff.name} {appt.staff.surname}
                        </div>
                    )}
                    {appt.note && (
                        <div className="mt-2 text-[11px] text-gray-400 dark:text-gray-500 italic line-clamp-1">{appt.note}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================
// ANA BILEŞEN: AdminCalendar
// ============================================================
const AdminCalendar = () => {
    const navigate = useNavigate();

    // --- STATE ---
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode, setViewMode] = useState("day"); // "day" | "week"
    const [selectedStaff, setSelectedStaff] = useState("all");
    const [zoomLevel, setZoomLevel] = useState(80);
    const [closedSlots, setClosedSlots] = useState([]);
    const [rawSlots, setRawSlots] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [servicesList, setServicesList] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Müşteri
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [isNewCustomer, setIsNewCustomer] = useState(false);
    const [customerSearch, setCustomerSearch] = useState("");

    // Saatler
    const [availableSlots, setAvailableSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);

    // Detay Modal
    const [selectedAppt, setSelectedAppt] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);

    // Hızlı Oluşturma Modal
    const [quickModalOpen, setQuickModalOpen] = useState(false);
    const [quickForm, setQuickForm] = useState({
        date: "", time: "", staffId: "", serviceId: "", optionalService1: "",
        customerId: "", name: "", surname: "", phone: "", birthDate: "", tag: "",
        note: "", isManual: false, customMinute: "00", customDuration: ""
    });
    const [serviceSearch, setServiceSearch] = useState("");
    const [showServiceDropdown, setShowServiceDropdown] = useState(false);
    const [optServiceSearch, setOptServiceSearch] = useState("");
    const [showOptServiceDropdown, setShowOptServiceDropdown] = useState(false);

    // Mola Modal
    const [molaModalOpen, setMolaModalOpen] = useState(false);
    const [molaForm, setMolaForm] = useState({
        staffId: "all", date: formatISODate(new Date()), selectedSlotIds: []
    });
    const [molaSlotsData, setMolaSlotsData] = useState([]);
    const [molaSlotsLoading, setMolaSlotsLoading] = useState(false);

    const [creating, setCreating] = useState(false);

    // Mobile personel seçim
    const [mobileStaffIdx, setMobileStaffIdx] = useState(0);

    // Timeline ref (auto-scroll)
    const timelineRef = useRef(null);

    // ============================================================
    // VERİ ÇEKME
    // ============================================================
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [staffRes, appRes, servRes, custRes] = await Promise.all([
                usersAPI.getStaff(),
                appointmentsAPI.getAll(),
                servicesAPI.getAll(),
                usersAPI.getCustomers(),
            ]);

            if (staffRes?.staff) setStaffList(staffRes.staff);
            if (appRes?.appointments) setAppointments(appRes.appointments);
            if (appRes?.closed_slots) setClosedSlots(appRes.closed_slots);
            if (servRes?.services) setServicesList(servRes.services);
            if (custRes?.customers) setCustomers(custRes.customers);
            else if (Array.isArray(custRes)) setCustomers(custRes);
        } catch (error) {
            console.error("Veri hatası:", error);
            toast.error("Veriler yüklenirken hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [fetchData]);

    // Mobilde haftalık görünüm yok
    useEffect(() => {
        if (isMobile) setViewMode("day");
    }, [isMobile]);

    // Mobilde tüm personel seçimi yok — ilk personele geç
    useEffect(() => {
        if (isMobile && selectedStaff === "all" && staffList.length > 0) {
            setSelectedStaff(String(staffList[0].id));
        }
    }, [isMobile, staffList]);

    // Auto-scroll to current hour
    useEffect(() => {
        if (!loading && timelineRef.current) {
            const currentHour = new Date().getHours();
            const scrollTo = Math.max((currentHour - BUSINESS_START - 1) * zoomLevel, 0);
            setTimeout(() => {
                timelineRef.current?.scrollTo({ top: scrollTo, behavior: "smooth" });
            }, 200);
        }
    }, [loading, viewMode]);

    // Müsait saatler: QuickModal açıldığında fetch
    useEffect(() => {
        const fetchSlots = async () => {
            if (!quickModalOpen || !quickForm.date || !quickForm.staffId) {
                setAvailableSlots([]);
                return;
            }
            setSlotsLoading(true);
            try {
                const response = await workScheduleAPI.getAvailableSlots(quickForm.staffId, quickForm.date);
                const slotsForDate = response.available_slots?.[quickForm.date] || [];
                setRawSlots(slotsForDate);

                const now = new Date();
                const selectedDateObj = new Date(quickForm.date);
                const isToday = selectedDateObj.toDateString() === now.toDateString();
                const curH = now.getHours(), curM = now.getMinutes();

                const filtered = slotsForDate
                    .filter(slot => {
                        const timeStr = slot.start_time || slot.time;
                        if (!timeStr) return false;
                        const isAvail = slot.is_available == true || slot.is_available === 1 || slot.is_available === "1";
                        if (!isAvail) return false;
                        if (isToday) {
                            const [h, m] = timeStr.split(":").map(Number);
                            if (h < curH || (h === curH && m <= curM)) return false;
                        }
                        return true;
                    })
                    .map(slot => slot.start_time || slot.time)
                    .sort();

                setAvailableSlots(filtered);
            } catch (err) {
                console.error("Saatler çekilemedi:", err);
                setAvailableSlots([]);
            } finally {
                setSlotsLoading(false);
            }
        };
        fetchSlots();
    }, [quickModalOpen, quickForm.date, quickForm.staffId]);

    // Mola slotları fetch
    useEffect(() => {
        const fetchMolaSlots = async () => {
            if (!molaModalOpen || !molaForm.staffId || !molaForm.date) {
                setMolaSlotsData([]);
                return;
            }
            setMolaSlotsLoading(true);
            try {
                const staffToFetch = molaForm.staffId === "all"
                    ? staffList
                    : staffList.filter(s => String(s.id) === String(molaForm.staffId));

                const results = await Promise.all(
                    staffToFetch.map(async (staff) => {
                        const response = await workScheduleAPI.getAvailableSlots(staff.id, molaForm.date);
                        const slotsForDate = response.available_slots?.[molaForm.date] || [];
                        const filtered = slotsForDate.filter(
                            slot => slot.is_available == true || slot.is_available === 1 || slot.is_available === "1"
                        );
                        return { staff, slots: filtered };
                    })
                );
                setMolaSlotsData(results.filter(r => r.slots.length > 0));
            } catch (err) {
                console.error("Mola saatleri çekilemedi:", err);
            } finally {
                setMolaSlotsLoading(false);
            }
        };
        fetchMolaSlots();
    }, [molaModalOpen, molaForm.staffId, molaForm.date, staffList]);

    // ============================================================
    // HESAPLAMALAR
    // ============================================================
    const daysToShow = useMemo(() => {
        const days = [];
        const start = new Date(currentDate);
        if (viewMode === "week") {
            const day = start.getDay();
            const diff = start.getDate() - day + (day === 0 ? -6 : 1);
            start.setDate(diff);
            for (let i = 0; i < 7; i++) {
                const d = new Date(start);
                d.setDate(start.getDate() + i);
                days.push(d);
            }
        } else {
            days.push(new Date(currentDate));
        }
        return days;
    }, [currentDate, viewMode]);

    const headerTitle = useMemo(() => {
        if (viewMode === "day") {
            return currentDate.toLocaleDateString("tr-TR", {
                weekday: "long", day: "numeric", month: "long", year: "numeric"
            });
        }
        const start = daysToShow[0];
        const end = daysToShow[daysToShow.length - 1];
        return `${start.getDate()} - ${end.getDate()} ${end.toLocaleDateString("tr-TR", { month: "long", year: "numeric" })}`;
    }, [currentDate, viewMode, daysToShow]);

    // Sahte mola randevuları (closed slots → fake appointment objesine)
    const fakeMolaAppointments = useMemo(() => {
        const molalar = closedSlots.filter(slot => {
            const slotDateStr = formatISODate(slot.work_date);
            const hasOverlap = appointments.some(appt => {
                if (formatISODate(appt.appointment_date) !== slotDateStr) return false;
                if (String(appt.staff_id) !== String(slot.staff_id)) return false;
                if (appt.status === "cancelled") return false;
                const appStartMin = timeToFloat(appt.appointment_time) * 60;
                const totalDur = parseInt(appt.appointment_duration) || 30;
                const slotStartMin = timeToFloat(slot.start_time) * 60;
                return slotStartMin >= appStartMin && slotStartMin < appStartMin + totalDur;
            });
            return !hasOverlap;
        });

        return molalar.map(mola => ({
            id: `mola-${mola.id}`,
            is_mola: true,
            appointment_date: mola.work_date,
            appointment_time: mola.start_time,
            appointment_duration: (timeToFloat(mola.end_time) - timeToFloat(mola.start_time)) * 60,
            staff_id: mola.staff_id,
            status: "mola",
            customer: { name: "MOLA", surname: "(KAPALI)" },
            service_name: "Personel Molası",
            mola_id: mola.id,
        }));
    }, [closedSlots, appointments]);

    const filteredAppointments = useMemo(() => {
        const allEvents = [...appointments, ...fakeMolaAppointments];
        return allEvents.filter(appt => {
            const apptDateStr = formatISODate(appt.appointment_date);
            const inRange = daysToShow.some(d => formatISODate(d) === apptDateStr);
            if (!inRange) return false;
            if (selectedStaff !== "all" && String(appt.staff_id) !== String(selectedStaff)) return false;
            if (appt.status === "cancelled") return false;
            return true;
        });
    }, [appointments, fakeMolaAppointments, daysToShow, selectedStaff]);

    // Modal hesaplamaları
    let modalTotalDuration = 0, modalMainName = "", modalOpt1Name = null, modalOpt2Name = null;
    if (selectedAppt) {
        if (selectedAppt.appointment_duration) {
            modalTotalDuration = parseInt(selectedAppt.appointment_duration);
        } else {
            const sDur = parseInt(selectedAppt.service?.duration || 0);
            const o1Dur = selectedAppt.optionalService1 ? parseInt(selectedAppt.optionalService1.duration || 0) : 0;
            const o2Dur = selectedAppt.optionalService2 ? parseInt(selectedAppt.optionalService2.duration || 0) : 0;
            modalTotalDuration = sDur + o1Dur + o2Dur;
        }
        modalMainName = selectedAppt.service_name || selectedAppt.service?.name;
        modalOpt1Name = selectedAppt.optional_service_name_1 || selectedAppt.optionalService1?.name;
        modalOpt2Name = selectedAppt.optional_service_name_2 || selectedAppt.optionalService2?.name;
    }

    // ============================================================
    // HANDLERS
    // ============================================================
    const handlePrev = () => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() - (viewMode === "week" ? 7 : 1));
        setCurrentDate(d);
    };

    const handleNext = () => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + (viewMode === "week" ? 7 : 1));
        setCurrentDate(d);
    };

    const handleToday = () => setCurrentDate(new Date());

    const handleZoom = (dir) => {
        if (dir === "in") setZoomLevel(prev => Math.min(prev + 20, 200));
        else setZoomLevel(prev => Math.max(prev - 20, 50));
    };

    const handleSlotClick = (day, hour, staffId) => {
        const todayZero = new Date(); todayZero.setHours(0, 0, 0, 0);
        const slotDayZero = new Date(day); slotDayZero.setHours(0, 0, 0, 0);
        if (slotDayZero < todayZero) {
            toast.warning("Geçmiş günlere işlem yapılamaz!");
            return;
        }
        setQuickForm({
            date: formatISODate(day),
            time: `${String(Math.floor(hour)).padStart(2, "0")}:00`,
            staffId: staffId ? String(staffId) : (selectedStaff !== "all" ? selectedStaff : ""),
            serviceId: "", optionalService1: "", customerId: "", name: "", surname: "",
            phone: "", note: "", isManual: false, customMinute: "00", customDuration: "",
            birthDate: "", tag: ""
        });
        setIsNewCustomer(false);
        setCustomerSearch(""); setFilteredCustomers([]);
        setServiceSearch(""); setShowServiceDropdown(false);
        setOptServiceSearch(""); setShowOptServiceDropdown(false);
        setQuickModalOpen(true);
    };

    const handleCustomerSearch = (e) => {
        const term = e.target.value;
        setCustomerSearch(term);
        if (term.length < 2) { setFilteredCustomers([]); return; }
        const filtered = customers.filter(c =>
            (c.name && c.name.toLowerCase().includes(term.toLowerCase())) ||
            (c.surname && c.surname.toLowerCase().includes(term.toLowerCase())) ||
            (c.phone && c.phone.includes(term))
        );
        setFilteredCustomers(filtered);
    };

    const selectCustomer = (customer) => {
        setQuickForm({ ...quickForm, customerId: customer.id, name: customer.name, surname: customer.surname, phone: customer.phone });
        setCustomerSearch(`${customer.name} ${customer.surname}`);
        setFilteredCustomers([]);
    };

    const handleAppointmentClick = useCallback((appt) => {
        if (appt.is_mola) {
            if (window.confirm("Bu molayı iptal edip tekrar randevuya açmak istiyor musunuz?")) {
                workScheduleAPI.toggleSlot(appt.mola_id).then(() => fetchData());
            }
            return;
        }
        setSelectedAppt(appt);
        setDetailsModalOpen(true);
    }, [fetchData]);

    // --- CRUD ---
    const handleQuickCreate = async () => {
        let rawPhone = quickForm.phone.trim().replace(/\s+/g, "");
        let formattedPhone = rawPhone;
        if (rawPhone) {
            if (!rawPhone.startsWith("+90")) {
                formattedPhone = "+90" + (rawPhone.startsWith("0") ? rawPhone.substring(1) : rawPhone);
            }
        }

        if (!quickForm.serviceId || !quickForm.staffId || !quickForm.time) {
            toast.error("Lütfen personel, hizmet ve saat bilgilerini eksiksiz doldurun.");
            return;
        }
        if (isNewCustomer && (!quickForm.name || !quickForm.surname || !formattedPhone)) {
            toast.error("Yeni kayıt için İsim, Soyisim ve Telefon zorunludur.");
            return;
        }
        if (!isNewCustomer && !quickForm.customerId) {
            toast.error("Lütfen bir müşteri seçin.");
            return;
        }

        setCreating(true);
        try {
            let finalCustomerId = quickForm.customerId;

            if (isNewCustomer) {
                const registerData = {
                    name: quickForm.name, surname: quickForm.surname, phone: formattedPhone,
                    email: "mail@giriniz.com", password: "123456!", password_confirmation: "123456!",
                    birth_date: quickForm.birthDate || "0001-01-01", tag: quickForm.tag || null, role: "customer"
                };
                const regRes = await authAPI.register(registerData);
                if (regRes?.user?.id) finalCustomerId = regRes.user.id;
                else if (regRes?.data?.user) finalCustomerId = regRes.data.user.id;
                else if (regRes?.id) finalCustomerId = regRes.id;
                else throw new Error("Müşteri oluşturuldu ama ID alınamadı.");
            }

            let finalTime = quickForm.time;
            if (quickForm.isManual) {
                if (!quickForm.time || !quickForm.customDuration) {
                    toast.error("Manuel modda saat ve özel süre zorunludur.");
                    setCreating(false);
                    return;
                }
                const baseHour = quickForm.time.split(":")[0];
                finalTime = `${baseHour}:${String(quickForm.customMinute).padStart(2, "0")}`;
            }

            const appointmentData = {
                customer_id: finalCustomerId, staff_id: quickForm.staffId,
                service_id: quickForm.serviceId, optional_service_id_1: quickForm.optionalService1 || null,
                appointment_date: quickForm.date, appointment_time: finalTime,
                note: quickForm.note, status: "approved",
                is_manual: quickForm.isManual ? 1 : 0,
                custom_duration: quickForm.isManual ? parseInt(quickForm.customDuration) : null
            };

            await appointmentsAPI.create(appointmentData);
            toast.success("Randevu başarıyla oluşturuldu!");
            setQuickModalOpen(false);
            fetchData();
        } catch (err) {
            console.error(err);
            toast.error("Hata: " + (err.response?.data?.message || err.message || "Bir hata oluştu"));
        } finally {
            setCreating(false);
        }
    };

    const handleToggleBreak = async () => {
        if (!quickForm.staffId || !quickForm.time) {
            toast.error("Lütfen bir saat seçin."); return;
        }
        const selectedSlotObj = rawSlots.find(s =>
            (s.start_time || s.time).substring(0, 5) === quickForm.time.substring(0, 5)
        );
        if (!selectedSlotObj) { toast.error("Seçili saat için geçerli bir slot bulunamadı."); return; }
        try {
            await workScheduleAPI.toggleSlot(selectedSlotObj.id);
            toast.success("Slot kapatıldı.");
            setQuickModalOpen(false);
            fetchData();
        } catch (err) {
            toast.error("Hata: Slot kapatılamadı.");
        }
    };

    const handleSaveMola = async () => {
        if (molaForm.selectedSlotIds.length === 0) {
            toast.warning("Lütfen mola için en az bir saat seçin."); return;
        }
        setCreating(true);
        try {
            await Promise.all(molaForm.selectedSlotIds.map(id => workScheduleAPI.toggleSlot(id)));
            toast.success("Molalar başarıyla oluşturuldu!");
            setMolaModalOpen(false);
            setMolaForm(prev => ({ ...prev, selectedSlotIds: [] }));
            fetchData();
        } catch (err) {
            toast.error("Hata: Molalar kaydedilemedi.");
        } finally {
            setCreating(false);
        }
    };

    const handleCancelAppointment = async () => {
        if (!selectedAppt) return;
        if (!window.confirm("Bu randevuyu İPTAL etmek istediğinize emin misiniz?")) return;
        try {
            await appointmentsAPI.updateStatus(selectedAppt.id, "cancelled");
            setAppointments(prev => prev.map(a => a.id === selectedAppt.id ? { ...a, status: "cancelled" } : a));
            setDetailsModalOpen(false);
            toast.success("Randevu iptal edildi.");
        } catch (err) {
            toast.error("İptal hatası: " + err.message);
        }
    };

    const handleNoShowAppointment = async () => {
        if (!selectedAppt) return;
        if (!window.confirm("Bu müşterinin randevuya GELMEDİĞİNİ onaylıyor musunuz?")) return;
        try {
            await appointmentsAPI.updateStatus(selectedAppt.id, "no_show");
            setAppointments(prev => prev.map(a => a.id === selectedAppt.id ? { ...a, status: "no_show" } : a));
            setDetailsModalOpen(false);
            toast.info("'Gelmedi' olarak işaretlendi.");
        } catch (err) {
            toast.error("İşlem hatası: " + err.message);
        }
    };

    const handleAddToBlacklist = async () => {
        if (!selectedAppt?.customer) return;
        const customerName = `${selectedAppt.customer.name} ${selectedAppt.customer.surname}`;
        if (!window.confirm(`${customerName} kara listeye eklenecek. Emin misiniz?`)) return;
        try {
            await usersAPI.update(selectedAppt.customer.id, { is_blacklisted: true });
            toast.warning(`${customerName} kara listeye alındı.`);
            setDetailsModalOpen(false);
        } catch (err) {
            toast.error("Hata: " + (err.response?.data?.message || err.message));
        }
    };

    // ============================================================
    // RENDER: TAKVİM GRID (Masaüstü)
    // ============================================================
    const renderCalendarGrid = () => {
        const hours = [];
        for (let i = BUSINESS_START; i < BUSINESS_END; i++) hours.push(i);

        let columns = [];
        let isStaffColumn = false;

        if (viewMode === "day" && selectedStaff === "all") {
            columns = staffList;
            isStaffColumn = true;
        } else {
            columns = daysToShow;
        }

        const colMinWidth = viewMode === "week" ? 160 : 200;

        return (
            <div
                ref={timelineRef}
                className="flex-1 overflow-auto"
                style={{ scrollbarWidth: "thin", minHeight: 0 }}
            >
                <div style={{ minWidth: `${60 + columns.length * colMinWidth}px` }}>

                    {/* HEADER (Yapışkan) */}
                    <div
                        className="flex sticky top-0 z-40 bg-white dark:bg-navy-800"
                        style={{ borderBottom: "1px solid var(--cal-border, rgba(148,163,184,0.15))" }}
                    >
                        {/* Sol köşe boşluğu */}
                        <div
                            className="shrink-0 sticky left-0 z-50 bg-white dark:bg-navy-800"
                            style={{ width: 60, borderRight: "1px solid var(--cal-border, rgba(148,163,184,0.15))" }}
                        ></div>

                        {/* Sütun başlıkları */}
                        {columns.map((col, idx) => {
                            const isStaffCol = isStaffColumn;
                            const title = isStaffCol ? `${col.name} ${col.surname}` : col.toLocaleDateString("tr-TR", { weekday: "short" });
                            const subTitle = isStaffCol ? (col.tag || "Personel") : col.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
                            const isToday = !isStaffCol && formatISODate(col) === formatISODate(new Date());

                            return (
                                <div
                                    key={idx}
                                    className="flex-1 flex flex-col items-center justify-center py-3 gap-0.5"
                                    style={{
                                        minWidth: colMinWidth,
                                        borderRight: "1px solid var(--cal-border, rgba(148,163,184,0.15))",
                                        background: isToday ? "rgba(67,24,255,0.04)" : "transparent"
                                    }}
                                >
                                    {isStaffCol ? (
                                        <>
                                            <div className="w-9 h-9 rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-400 font-bold text-sm flex items-center justify-center mb-1">
                                                {getInitials(col.name, col.surname)}
                                            </div>
                                            <div className="text-xs font-bold text-navy-700 dark:text-white truncate max-w-[90%] text-center">{title}</div>
                                            <div className="text-[10px] text-gray-400 dark:text-gray-500">{subTitle}</div>
                                        </>
                                    ) : (
                                        <>
                                            <div
                                                className={`text-xs font-bold uppercase tracking-wide ${isToday ? "text-brand-500" : "text-gray-500 dark:text-gray-400"}`}
                                            >{title}</div>
                                            <div
                                                className={`text-lg font-extrabold ${isToday ? "text-brand-500" : "text-navy-700 dark:text-white"}`}
                                            >{col.getDate()}</div>
                                            {isToday && <div className="w-1.5 h-1.5 rounded-full bg-brand-500"></div>}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* GÖVDE */}
                    <div>
                        {hours.map(h => (
                            <div key={h} className="flex" style={{ borderBottom: "1px solid var(--cal-border, rgba(148,163,184,0.10))" }}>

                                {/* Saat etiketi (yapışkan sol) */}
                                <div
                                    className="shrink-0 sticky left-0 z-20 bg-white dark:bg-navy-800 flex items-start justify-end pr-3 pt-2"
                                    style={{
                                        width: 60, minHeight: zoomLevel,
                                        borderRight: "1px solid var(--cal-border, rgba(148,163,184,0.15))"
                                    }}
                                >
                  <span className="text-[11px] font-bold text-gray-400 dark:text-gray-600 -mt-1">
                    {String(h).padStart(2, "0")}:00
                  </span>
                                </div>

                                {/* Hücreler */}
                                {columns.map((col, idx) => {
                                    const colDate = isStaffColumn ? currentDate : col;
                                    const colStaffId = isStaffColumn ? col.id : (selectedStaff !== "all" ? selectedStaff : null);

                                    const slotAppointments = filteredAppointments.filter(appt => {
                                        if (formatISODate(appt.appointment_date) !== formatISODate(colDate)) return false;
                                        if (colStaffId && String(appt.staff_id) !== String(colStaffId)) return false;
                                        const apptHour = parseInt((appt.appointment_time || "00:00").split(":")[0]);
                                        return apptHour === h;
                                    });

                                    const isToday = !isStaffColumn && formatISODate(col) === formatISODate(new Date());
                                    const isPast = (() => {
                                        const todayZero = new Date(); todayZero.setHours(0, 0, 0, 0);
                                        const colZero = new Date(colDate); colZero.setHours(0, 0, 0, 0);
                                        return colZero < todayZero;
                                    })();

                                    return (
                                        <div
                                            key={idx}
                                            className="flex-1 relative group"
                                            style={{
                                                minWidth: colMinWidth, minHeight: zoomLevel,
                                                borderRight: "1px solid var(--cal-border, rgba(148,163,184,0.10))",
                                                background: isToday ? "rgba(67,24,255,0.02)" : isPast ? "rgba(148,163,184,0.03)" : "transparent",
                                                cursor: isPast ? "not-allowed" : "cell",
                                            }}
                                            onClick={() => !isPast && handleSlotClick(colDate, h, colStaffId)}
                                        >
                                            {/* Hover ekleme ipucu */}
                                            {!isPast && (
                                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <span className="text-[10px] font-bold text-brand-500 bg-white dark:bg-navy-800 px-2 py-1 rounded-lg shadow-sm border border-brand-100 dark:border-brand-900/50">
                            + Ekle
                          </span>
                                                </div>
                                            )}

                                            {/* Randevu kartları (absolute) */}
                                            <div className="absolute inset-0 pointer-events-none">
                                                {slotAppointments.map((appt, i) => (
                                                    <div key={appt.id} className="pointer-events-auto">
                                                        <AppointmentCard
                                                            appt={appt}
                                                            zoomLevel={zoomLevel}
                                                            count={slotAppointments.length}
                                                            index={i}
                                                            onClick={handleAppointmentClick}
                                                            isStaffColumn={isStaffColumn}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================
    // RENDER: MOBİL GÖRÜNÜM
    // ============================================================
    const renderMobileView = () => {
        // Mobilde tek personel seçimi zorunlu
        const mobileStaff = staffList.find(s => String(s.id) === String(selectedStaff));

        const mobileAppointments = filteredAppointments
            .filter(appt => formatISODate(appt.appointment_date) === formatISODate(currentDate))
            .sort((a, b) => {
                const tA = a.appointment_time || "00:00";
                const tB = b.appointment_time || "00:00";
                return tA.localeCompare(tB);
            });

        return (
            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ scrollbarWidth: "thin" }}>
                {/* Personel seçici (mobil) */}
                {staffList.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
                        {staffList.map(s => (
                            <button
                                key={s.id}
                                onClick={() => setSelectedStaff(String(s.id))}
                                className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-2xl text-xs font-bold border transition-all
                  ${String(selectedStaff) === String(s.id)
                                    ? "bg-brand-500 text-white border-brand-500 shadow-lg shadow-brand-500/30"
                                    : "bg-white dark:bg-navy-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-navy-700"}`}
                            >
                                <div className={`w-6 h-6 rounded-lg flex items-center justify-center font-bold text-[10px] ${String(selectedStaff) === String(s.id) ? "bg-white/20" : "bg-brand-500/10"}`}>
                                    {getInitials(s.name, s.surname)}
                                </div>
                                {s.name} {s.surname}
                            </button>
                        ))}
                    </div>
                )}

                {/* Tarih gezgini (mobil) */}
                <div className="flex items-center gap-2 bg-white dark:bg-navy-800 rounded-2xl p-3 shadow-sm border border-gray-100 dark:border-navy-700">
                    <button onClick={handlePrev} className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors text-gray-500">
                        <FiChevronLeft size={18} />
                    </button>
                    <div className="flex-1 text-center">
                        <div className="font-bold text-navy-700 dark:text-white text-sm">
                            {currentDate.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}
                        </div>
                        {formatISODate(currentDate) !== formatISODate(new Date()) && (
                            <button onClick={handleToday} className="text-[11px] text-brand-500 font-bold mt-0.5">Bugüne Dön</button>
                        )}
                    </div>
                    <button onClick={handleNext} className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors text-gray-500">
                        <FiChevronRight size={18} />
                    </button>
                </div>

                {/* Randevu sayacı + Ekle butonu */}
                <div className="flex items-center justify-between">
                    <div className="text-sm font-bold text-navy-700 dark:text-white">
                        {mobileAppointments.length > 0
                            ? `${mobileAppointments.length} randevu`
                            : "Randevu yok"}
                    </div>
                    <button
                        onClick={() => handleSlotClick(currentDate, new Date().getHours(), selectedStaff !== "all" ? selectedStaff : null)}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-brand-500 hover:bg-brand-600 shadow-md shadow-brand-500/30 transition-all"
                    >
                        <FiPlus size={14} /> Yeni Randevu
                    </button>
                </div>

                {/* Liste */}
                {mobileAppointments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-navy-700 flex items-center justify-center mb-4">
                            <FiCalendar size={28} className="text-gray-300 dark:text-gray-600" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">Bu gün için randevu bulunmuyor.</p>
                        <p className="text-gray-400 dark:text-gray-600 text-xs mt-1">Yukarıdaki butona basarak yeni ekleyebilirsiniz.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {mobileAppointments.map(appt => (
                            <MobileAppointmentCard
                                key={appt.id}
                                appt={appt}
                                onPress={handleAppointmentClick}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // ============================================================
    // ANA RENDER
    // ============================================================
    return (
        <>
            <ToastContainer position="bottom-right" theme="colored" toastClassName="!rounded-2xl !font-bold" />

            {/* Global style */}
            <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cal-fade-in { animation: fadeInUp 0.3s ease-out; }

        /* Dark mode değişkenleri */
        .dark { --cal-border: rgba(148,163,184,0.12); }
        html:not(.dark) { --cal-border: rgba(148,163,184,0.2); }

        /* Thin scrollbar */
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.3); border-radius: 99px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.5); }
      `}</style>

            <div
                className="flex flex-col bg-white dark:bg-navy-800 rounded-[20px] overflow-hidden shadow-sm border border-gray-100 dark:border-navy-700 cal-fade-in"
                style={{ height: "calc(100vh - 100px)", minHeight: 500 }}
            >
                {/* ====== HEADER ====== */}
                <div className="shrink-0 px-4 sm:px-5 py-3 flex items-center justify-between gap-3 border-b border-gray-100 dark:border-navy-700 bg-gray-50/50 dark:bg-navy-900/50">

                    {/* Sol: Navigasyon */}
                    <div className="flex items-center gap-2">
                        {/* Prev/Next */}
                        <div className="flex items-center gap-1 bg-white dark:bg-navy-800 rounded-xl p-1 shadow-sm border border-gray-100 dark:border-navy-700">
                            <button
                                onClick={handlePrev}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-all"
                            >
                                <FiChevronLeft size={18} />
                            </button>
                            <button
                                onClick={handleNext}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-all"
                            >
                                <FiChevronRight size={18} />
                            </button>
                        </div>

                        {/* Başlık */}
                        <div className="hidden sm:block">
                            <h2 className="font-bold text-navy-700 dark:text-white text-sm md:text-base leading-tight">
                                {headerTitle}
                            </h2>
                        </div>

                        {/* Bugün butonu */}
                        {formatISODate(currentDate) !== formatISODate(new Date()) && (
                            <button
                                onClick={handleToday}
                                className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold bg-brand-50 dark:bg-brand-500/10 text-brand-500 hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-all border border-brand-100 dark:border-brand-500/20"
                            >
                                Bugün
                            </button>
                        )}
                    </div>

                    {/* Orta: Görünüm seçici + Personel (masaüstü) */}
                    {!isMobile && (
                        <div className="flex items-center gap-2">
                            {/* Görünüm */}
                            <div className="flex bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-navy-600 overflow-hidden shadow-sm">
                                {[{ val: "day", icon: <FiCalendar size={14}/>, label: "Günlük" }, { val: "week", icon: <FiGrid size={14}/>, label: "Haftalık" }].map(v => (
                                    <button
                                        key={v.val}
                                        onClick={() => setViewMode(v.val)}
                                        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all
                      ${viewMode === v.val
                                            ? "bg-brand-500 text-white"
                                            : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-navy-700"}`}
                                    >
                                        {v.icon} <span className="hidden md:inline">{v.label}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Personel seçici */}
                            <div className="relative">
                                <FiUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13}/>
                                <select
                                    value={selectedStaff}
                                    onChange={e => setSelectedStaff(e.target.value)}
                                    className="pl-8 pr-8 py-2 bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-600 rounded-xl text-xs font-bold text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all appearance-none cursor-pointer shadow-sm"
                                    style={{ minWidth: 160 }}
                                >
                                    <option value="all">Tüm Personeller</option>
                                    {staffList.map(s => (
                                        <option key={s.id} value={s.id}>{s.name} {s.surname}</option>
                                    ))}
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-[10px]">▾</div>
                            </div>
                        </div>
                    )}

                    {/* Sağ: Zoom + Mola + Yenile */}
                    <div className="flex items-center gap-1.5">
                        {!isMobile && (
                            <div className="flex items-center gap-1 bg-white dark:bg-navy-800 rounded-xl p-1 shadow-sm border border-gray-100 dark:border-navy-700">
                                <button
                                    onClick={() => handleZoom("out")}
                                    className="p-1.5 rounded-lg text-gray-500 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-all"
                                    title="Küçült"
                                >
                                    <FiZoomOut size={15} />
                                </button>
                                <span className="text-[10px] font-bold text-gray-400 px-1">{zoomLevel}px</span>
                                <button
                                    onClick={() => handleZoom("in")}
                                    className="p-1.5 rounded-lg text-gray-500 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-all"
                                    title="Büyüt"
                                >
                                    <FiZoomIn size={15} />
                                </button>
                            </div>
                        )}

                        {/* Mola butonu */}
                        <button
                            onClick={() => {
                                setMolaForm({
                                    staffId: selectedStaff !== "all" ? selectedStaff : "all",
                                    date: formatISODate(currentDate),
                                    selectedSlotIds: []
                                });
                                setMolaModalOpen(true);
                            }}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-red-500 border border-red-200 dark:border-red-800/50 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all bg-white dark:bg-navy-800 shadow-sm"
                        >
                            <BsBan size={14} />
                            <span className="hidden sm:inline">Mola Ekle</span>
                        </button>

                        {/* Yenile */}
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="p-2 rounded-xl text-gray-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-all bg-white dark:bg-navy-800 border border-gray-100 dark:border-navy-700 shadow-sm"
                            title="Yenile"
                        >
                            <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
                        </button>
                    </div>
                </div>

                {/* ====== BODY ====== */}
                {loading ? (
                    <CalendarSkeleton />
                ) : isMobile ? (
                    renderMobileView()
                ) : (
                    renderCalendarGrid()
                )}
            </div>

            {/* ====== MODALLER ====== */}
            {detailsModalOpen && selectedAppt && (
                <AppointmentDetailModal
                    appt={selectedAppt}
                    onClose={() => setDetailsModalOpen(false)}
                    onCancel={handleCancelAppointment}
                    onNoShow={handleNoShowAppointment}
                    onAddToBlacklist={handleAddToBlacklist}
                />
            )}

            {quickModalOpen && (
                <QuickCreateModal
                    open={quickModalOpen}
                    onClose={() => setQuickModalOpen(false)}
                    quickForm={quickForm}
                    setQuickForm={setQuickForm}
                    servicesList={servicesList}
                    staffList={staffList}
                    availableSlots={availableSlots}
                    slotsLoading={slotsLoading}
                    isNewCustomer={isNewCustomer}
                    setIsNewCustomer={setIsNewCustomer}
                    customerSearch={customerSearch}
                    setCustomerSearch={setCustomerSearch}
                    filteredCustomers={filteredCustomers}
                    handleCustomerSearch={handleCustomerSearch}
                    selectCustomer={selectCustomer}
                    serviceSearch={serviceSearch}
                    setServiceSearch={setServiceSearch}
                    showServiceDropdown={showServiceDropdown}
                    setShowServiceDropdown={setShowServiceDropdown}
                    optServiceSearch={optServiceSearch}
                    setOptServiceSearch={setOptServiceSearch}
                    showOptServiceDropdown={showOptServiceDropdown}
                    setShowOptServiceDropdown={setShowOptServiceDropdown}
                    creating={creating}
                    onSubmit={handleQuickCreate}
                    onToggleBreak={handleToggleBreak}
                />
            )}

            {molaModalOpen && (
                <BreakModal
                    open={molaModalOpen}
                    onClose={() => setMolaModalOpen(false)}
                    staffList={staffList}
                    molaForm={molaForm}
                    setMolaForm={setMolaForm}
                    molaSlotsData={molaSlotsData}
                    molaSlotsLoading={molaSlotsLoading}
                    creating={creating}
                    onSave={handleSaveMola}
                />
            )}
        </>
    );
};

export default AdminCalendar;